import '@fortawesome/fontawesome-free/css/all.min.css';
import * as Sentry from '@sentry/browser';
import { initGAListener } from './analytics.js';

document.addEventListener('livewire:init', initGAListener);

// Session Replay records every session so the team can watch exactly what a guest
// did when reproducing reported issues. Sample rates are env-driven with a 100%
// default; lower the session rate per deploy if quota becomes a concern.
const replaysSessionSampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 1.0
);
const replaysOnErrorSampleRate = Number(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0
);

if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 1.0),
        sendDefaultPii: true,
        replaysSessionSampleRate,
        replaysOnErrorSampleRate,
        integrations: [
            // Record replays with NO masking so the team can read on-screen text when
            // reproducing guest-reported issues. Card numbers are entered in the payment
            // provider's iframe (outside this DOM), so unmasking here does not expose PANs.
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                // Everything stays readable for debugging EXCEPT password fields — guest
                // credentials must never reach Sentry. Matched by type and by the stable
                // ids used on the login/register/reset/onboarding forms (robust even if a
                // show/hide toggle flips the input's type to "text").
                mask: ['input[type="password"]', '#password', '#password_confirmation'],
            }),
        ],
    });

    // Tag every event so bk is distinguishable from other apps in the shared org.
    Sentry.setTag('app', 'bk');
}

document.addEventListener('alpine:init', () => {
    Alpine.data('focusTrap', () => ({
        previousFocus: null,
        trapHandler: null,

        init() {
            this.previousFocus = window._lastModalTrigger ?? null;
            document.body.classList.add('overflow-hidden');
            this.$nextTick(() => {
                // querySelectorAll matches DOM presence, not visibility — an x-show'd
                // element (e.g. a closed confirm popover nested inside this modal)
                // stays in the DOM with display:none but still has tabIndex !== -1,
                // so without a visibility check it can end up being treated as the
                // trap's "last" element. The browser then skips it when tabbing
                // forward (display:none isn't tab-reachable), activeElement never
                // equals our computed "last", and focus escapes the trap entirely.
                // offsetWidth/offsetHeight/getClientRects() is the standard cheap
                // visibility check (what jQuery's :visible uses) — unlike
                // offsetParent, it isn't falsely zero for position:fixed elements
                // (the confirm popover uses fixed), so it works for both.
                const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
                const getFocusable = () =>
                    Array.from(this.$el.querySelectorAll(
                        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
                    )).filter(el => el.tabIndex !== -1 && isVisible(el));

                // Focus the container itself (not the first focusable element) so
                // nothing shows a visible focus ring when the modal opens — Tab
                // still moves to the first real element next, same as if it were
                // focused directly, since programmatic focus on a tabindex="-1"
                // element doesn't trigger :focus-visible.
                this.$el.setAttribute('tabindex', '-1');
                this.$el.style.outline = 'none';
                this.$el.focus({ preventScroll: true });

                this.trapHandler = (e) => {
                    if (e.key !== 'Tab') return;
                    const focusable = getFocusable();
                    if (!focusable.length) return;
                    const first = focusable[0], last = focusable[focusable.length - 1];
                    const atStart = document.activeElement === first || document.activeElement === this.$el;
                    const atEnd = document.activeElement === last || document.activeElement === this.$el;
                    if (e.shiftKey ? atStart : atEnd) {
                        e.preventDefault();
                        (e.shiftKey ? last : first).focus();
                    }
                };
                document.addEventListener('keydown', this.trapHandler);
            });
        },

        destroy() {
            document.body.classList.remove('overflow-hidden');
            if (this.trapHandler) document.removeEventListener('keydown', this.trapHandler);
            if (this.previousFocus && document.contains(this.previousFocus)) this.previousFocus.focus();
        },
    }));

    // Registered by name (rather than inlined as a literal x-data="{...}" object
    // in the Blade template) so the root element's x-data attribute text is 100%
    // static ("scheduleCanvas()") across every Livewire re-render. When the
    // schedule/portal Schedule page previously baked @js($availableSchedules) and
    // @js($cardRanges) directly into the x-data literal, that attribute's text
    // differed on nearly every render (booking a class changes availability
    // data), so Livewire's morph treated x-data as "changed" and re-processed
    // this element's directive tree via a mutation-triggered re-init. Partway
    // through that re-walk, child directives referencing slotModal/card were
    // evaluated before the new scope was attached, throwing a ReferenceError
    // that silently aborted the rest of the walk — leaving every directive
    // after that point (the entire class-details modal) never bound to
    // reactivity at all. Reading the dynamic data from data-* attributes
    // instead (which can freely change without triggering any x-data
    // reprocessing) avoids the whole failure mode.
    Alpine.data('scheduleCanvas', () => ({
        open: false,
        card: {},
        previousFocus: null,
        trapHandler: null,
        slotModal: { open: false, date: null, hour: null, dateLabel: '', timeLabel: '', courses: [] },
        bookingModalOpen: false,
        availableSchedules: {},
        cardRanges: {},
        browseLinkHref: '#',

        init() {
            this.availableSchedules = JSON.parse(this.$el.dataset.availableSchedules || '{}');
            this.cardRanges = JSON.parse(this.$el.dataset.cardRanges || '{}');
            this.browseLinkHref = this.$el.dataset.browseLinkHref || '#';
        },

        show(c) {
            this.previousFocus = window._lastModalTrigger ?? document.activeElement;
            this.card = c;
            this.open = true;
            document.body.classList.add('overflow-hidden');
            this.$nextTick(() => {
                const getFocusable = () => Array.from(this.$refs.modal?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])') ?? []).filter(el => el.tabIndex !== -1);
                const els = getFocusable();
                if (els.length) els[0].focus();
                this.trapHandler = (e) => {
                    if (e.key !== 'Tab') return;
                    const focusable = getFocusable();
                    if (!focusable.length) return;
                    const first = focusable[0], last = focusable[focusable.length - 1];
                    if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
                        e.preventDefault();
                        (e.shiftKey ? last : first).focus();
                    }
                };
                document.addEventListener('keydown', this.trapHandler);
            });
        },

        close() {
            this.open = false;
            document.body.classList.remove('overflow-hidden');
            if (this.trapHandler) { document.removeEventListener('keydown', this.trapHandler); this.trapHandler = null; }
            if (this.previousFocus && document.contains(this.previousFocus)) this.previousFocus.focus();
            setTimeout(() => this.card = {}, 200);
        },

        isPast(dateKey, hour) {
            const now = new Date();
            const today = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
            if (dateKey < today) return true;
            if (dateKey === today && hour < now.getHours()) return true;
            return false;
        },

        openSlot(date, hour, dateLabel) {
            const allForDay = this.availableSchedules[date] ?? [];
            const courses = allForDay.filter(s => s.start_hour <= hour && s.end_hour > hour);
            if (courses.length === 0 || this.isPast(date, hour)) return;
            const h = hour % 12 === 0 ? 12 : hour % 12;
            const ampm = hour < 12 ? 'AM' : 'PM';
            this.slotModal = { open: true, date, hour, dateLabel, timeLabel: h + ':00 ' + ampm, courses };
            document.body.classList.add('overflow-hidden');
        },

        closeSlot() {
            this.slotModal.open = false;
            document.body.classList.remove('overflow-hidden');
            setTimeout(() => { this.slotModal.courses = []; this.bookingModalOpen = false; }, 150);
        },

        coursesUrl(scheduleId) {
            const u = new URL(this.browseLinkHref, window.location.href);
            u.searchParams.set('open_schedule', scheduleId);
            return u.toString();
        },
    }));
});

// Portal pages (Profile, Guests, ...) call this from a feedback banner's x-init
// so a scroll-to-top doesn't leave the errored field tucked behind the sticky
// header. Prefer the first invalid field, landed a third of the way down the
// viewport (not dead center) so there's enough clearance above it for the
// sticky header, and fall back to the banner itself when there's no field error.
window.scrollToPortalFeedback = function (fallbackEl) {
    setTimeout(() => {
        const target = document.querySelector('.border-red-400') || fallbackEl;
        if (!target) return;

        const rect = target.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - (window.innerHeight / 3);
        window.scrollTo({ top: Math.max(targetY, 0), behavior: 'smooth' });
    }, 100);
};
